import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { authFetch } from '../utils/authFetch';
import {
  Container, Stack, Card, Group, Text, Button, Textarea,
  Badge, Divider, Alert, Stepper,
  ThemeIcon, Switch, ActionIcon, Tooltip, RingProgress,
  Accordion, Paper, SimpleGrid, ScrollArea, Center
} from '@mantine/core';
import {
  IconCompass, IconCheck, IconAlertCircle,
  IconFileText, IconBuildingBank,
  IconArrowLeft, IconShieldCheck, IconChartBar
} from '@tabler/icons-react';

import ExplorerNarrativeCard from './components/ExplorerNarrativeCard';
import ExplorerRiskAutopilot from './ExplorerRiskAutopilot';
import AIGeneratingIcon from '../components/AIGeneratingIcon';
import DocumentUpload from '../components/DocumentUpload';
import StepContent from '../components/StepContent';
import { fetchContextData } from '../redux/actions/contextActions';
import {
  selectAssessmentById,
  updateAssessmentStatusByUuid,
  setAssessmentPosition,
  updateAssessmentContext,
  initializeAssessmentLocal
} from '../redux/slices/assessmentsSlice';
import {
  setCurrentAssessment,
  setCurrentModel
} from '../redux/slices/appSlice';
import {
  selectExplorerRisks,
  selectSavedExplorerRisks
} from '../redux/slices/risksSlice';

/**
 * ExplorerAutopilotFlow - Main orchestrator for Explorer assessments
 * Guides user through: Narrative → Risks → Review → Report
 * No upload step - inherits model card from parent Builder assessment
 */
export default function ExplorerAutopilotFlow() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { modelId, assessmentUuid } = useParams();
  const { isLoading: authLoading, isAuthenticated } = useAuth0();

  // Redux state
  const assessment = useSelector(state => selectAssessmentById(state, assessmentUuid));
  const modelName = useSelector(state => state.models?.byId?.[modelId]?.model_name || 'Unknown Model');
  const parentAssessmentUuid = assessment?.parent_assessment_uuid;
  const businessContext = assessment?.business_context || '';
  const useCase = assessment?.use_case || '';

  // Template context fields for dynamic rendering
  const templateContextFields = assessment?.templateConfig?.taxonomy?.contextFields?.businessProfile;
  // Context data from Redux context slice (populated by StepContent auto-save)
  const contextData = useSelector(state => state.context?.data?.[modelId] || {});
  const businessProfileData = useMemo(() => contextData?.businessProfile || {}, [contextData?.businessProfile]);

  // Get parent assessment info for inherited context
  const parentAssessment = useSelector(state =>
    parentAssessmentUuid ? selectAssessmentById(state, parentAssessmentUuid) : null
  );
  const inheritedBucket = parentAssessment?.bucket || assessment?.bucket;
  const inheritedPath = parentAssessment?.path || assessment?.path;

  // Explorer risks from Redux
  const explorerRisks = useSelector(state => selectExplorerRisks(state, assessmentUuid));
  const savedRisks = useSelector(state => selectSavedExplorerRisks(state, assessmentUuid));

  // Step workflow state
  const [currentStep, setCurrentStep] = useState('describeBusinessCase');
  const [businessCaseDescription, setBusinessCaseDescription] = useState('');
  const [businessCaseDocuments, setBusinessCaseDocuments] = useState([]);
  const [approvedNarrative, setApprovedNarrative] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  // Initialize autonomous from saved assessment setting (DB stores 1 for true, 0 for false)
  const savedAutonomous = assessment?.info?.autonomous;
  const [autonomous, setAutonomous] = useState(savedAutonomous === true || savedAutonomous === 1);
  const [currentAreaProgress, setCurrentAreaProgress] = useState({
    currentArea: null,
    phase: null,
    completedAreas: 0,
    totalAreas: 6  // Explorer has 6 organizational risk areas
  });

  // Explorer steps
  const steps = [
    { key: 'describeBusinessCase', label: 'Your Business', icon: IconBuildingBank },
    { key: 'narrative', label: 'Context & Narrative', icon: IconCompass },
    { key: 'risks', label: 'Risk Analysis', icon: IconBuildingBank },
    { key: 'review', label: 'Review & Refine', icon: IconCheck },
    { key: 'report', label: 'Generate Report', icon: IconFileText }
  ];

  const currentStepIndex = steps.findIndex(s => s.key === currentStep);

  // Explorer organizational risk areas (6 areas, no Security)
  const explorerRiskAreas = useMemo(() => ({
    commercialRisk: {
      label: 'Commercial Risk',
      description: 'Revenue, market position, customer trust',
      riskSources: [
        { name: 'Revenue Impact', description: 'Effects on revenue streams' },
        { name: 'Customer Trust', description: 'Impact on customer confidence' },
        { name: 'Market Position', description: 'Effects on competitive standing' }
      ]
    },
    operationalRisk: {
      label: 'Operational Risk',
      description: 'Service delivery, processes, dependencies',
      riskSources: [
        { name: 'Service Disruption', description: 'Risks of service outages' },
        { name: 'Process Dependencies', description: 'Critical dependencies on AI' },
        { name: 'Resource Allocation', description: 'Impact on staffing and resources' }
      ]
    },
    complianceRisk: {
      label: 'Compliance & Legal',
      description: 'Regulatory requirements, legal liability',
      riskSources: [
        { name: 'Regulatory Requirements', description: 'Applicable regulations' },
        { name: 'Legal Liability', description: 'Potential legal exposure' },
        { name: 'Data Protection', description: 'Privacy compliance' }
      ]
    },
    societalRisk: {
      label: 'Societal & Ethical',
      description: 'Social impact, ethics, human rights',
      riskSources: [
        { name: 'Workforce Impact', description: 'Effects on employees and jobs' },
        { name: 'Ethical Concerns', description: 'Ethical implications' },
        { name: 'Human Rights', description: 'Effects on rights and freedoms' }
      ]
    },
    environmentalRisk: {
      label: 'Environmental',
      description: 'Sustainability, carbon footprint, ESG',
      riskSources: [
        { name: 'Carbon Footprint', description: 'Direct and indirect emissions' },
        { name: 'Resource Consumption', description: 'Energy and materials usage' },
        { name: 'ESG Commitments', description: 'Alignment with ESG goals' }
      ]
    },
    reputationalRisk: {
      label: 'Reputational',
      description: 'Stakeholder trust, brand integrity',
      riskSources: [
        { name: 'Public Perception', description: 'How public views AI deployment' },
        { name: 'Media Exposure', description: 'Risk of negative coverage' },
        { name: 'Brand Integrity', description: 'Impact on brand values' }
      ]
    }
  }), []);

  // Initialize assessment context and load template config
  useEffect(() => {
    const initExplorer = async () => {
      try {
        if (modelId && assessmentUuid) {
          dispatch(setCurrentModel(modelId));
          dispatch(setCurrentAssessment({
            modelId,
            assessmentUuid,
            assessmentType: 'business_case'
          }));

          // Fetch saved context data for StepContent
          dispatch(fetchContextData(modelId, assessmentUuid));

          // Fetch template config if not already loaded
          const templateId = assessment?.info?.template_id;
          if (templateId && !assessment?.templateConfig) {
            try {
              const resp = await authFetch('/.netlify/functions/templates-getTemplate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: templateId })
              });
              if (resp.ok) {
                const template = await resp.json();
                dispatch(initializeAssessmentLocal({
                  assessmentUuid,
                  modelId,
                  templateId,
                  templateConfig: template,
                }));
              }
            } catch (templateErr) {
              console.warn('[ExplorerFlow] Failed to fetch template config:', templateErr);
            }
          }

          // Restore business case description from legacy context or context slice
          const savedDescription = assessment?.context?.businessCaseProfile?.businessCaseDescription;
          if (savedDescription) {
            setBusinessCaseDescription(savedDescription);
          }

          // Resume at correct step based on saved position
          const savedStep = assessment?.position?.majorStep;
          if (savedStep && savedStep !== 'describeBusinessCase') {
            setCurrentStep(savedStep);
          }

          setError(null);
          setIsLoading(false);
        } else {
          setError('Missing model ID or assessment UUID');
          setIsLoading(false);
        }
      } catch (err) {
        console.error('[ExplorerAutopilotFlow] Initialization error:', err);
        setError(`Failed to initialize Explorer assessment: ${err.message}`);
        setIsLoading(false);
      }
    };

    initExplorer();
  }, [dispatch, modelId, assessmentUuid, assessment?.context?.businessCaseProfile?.businessCaseDescription, assessment?.position?.majorStep, assessment?.info?.template_id, assessment?.templateConfig]);

  // Handle business case description submission
  const handleBusinessCaseSubmit = useCallback(() => {
    // When using template fields, pull the main description from context slice
    const description = templateContextFields
      ? (businessProfileData?.businessDescription || '').trim()
      : businessCaseDescription.trim();

    if (!description) return;

    // Save all context fields (StepContent auto-saves, but also persist to assessment context)
    const contextPayload = templateContextFields
      ? { ...businessProfileData, businessCaseDescription: description }
      : { businessCaseDescription: description };

    dispatch(updateAssessmentContext({
      assessmentUuid,
      section: 'businessCaseProfile',
      data: contextPayload
    }));

    dispatch(setAssessmentPosition({
      modelId,
      assessmentUuid,
      position: { majorStep: 'narrative', stepCompleted: 'describeBusinessCase' }
    }));

    setCurrentStep('narrative');
  }, [dispatch, modelId, assessmentUuid, businessCaseDescription, templateContextFields, businessProfileData]);

  // Handle narrative approval
  const handleNarrativeApproved = useCallback((narrative) => {
    console.log('[ExplorerAutopilotFlow] Narrative approved, moving to risk analysis');
    setApprovedNarrative(narrative);
    setCurrentStep('risks');
  }, []);

  // Handle risk analysis completion - mark assessment as Completed
  const handleRiskAnalysisComplete = useCallback(async () => {
    console.log('[ExplorerAutopilotFlow] Risk analysis complete, marking assessment as Completed');
    try {
      // Update position to indicate completion
      await dispatch(setAssessmentPosition({
        modelId,
        assessmentUuid,
        position: {
          majorStep: 'review',
          stepCompleted: 'risks'
        }
      }));

      // Mark assessment status as Completed
      await dispatch(updateAssessmentStatusByUuid({
        modelId,
        assessmentUuid,
        status: 'Completed',
        assessmentType: 'explorer'  // Required to update Explorer (not Builder) assessment
      })).unwrap();

      console.log('[ExplorerAutopilotFlow] Assessment status updated to Completed');
    } catch (err) {
      console.error('[ExplorerAutopilotFlow] Failed to update assessment status:', err);
    }

    // Move to review step
    setCurrentStep('review');
  }, [dispatch, modelId, assessmentUuid]);

  // Navigation handlers
  const handleBackToInventory = () => {
    navigate('/inventory');
  };

  const handleStepClick = (stepKey) => {
    const clickedIndex = steps.findIndex(s => s.key === stepKey);
    // Only allow going back or to completed steps
    if (clickedIndex <= currentStepIndex) {
      setCurrentStep(stepKey);
    }
  };

  // Show AI robot loading screen while authenticating
  if (authLoading || !isAuthenticated) {
    return (
      <Center h="100vh">
        <Stack align="center" gap="xl">
          <AIGeneratingIcon isGenerating size={64} color="#228be6" />
          <Text size="lg" c="dimmed" fw={500}>
            Loading assessment...
          </Text>
        </Stack>
      </Center>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <Container size="lg" py="xl">
        <Stack align="center" gap="md">
          <AIGeneratingIcon isGenerating size={48} />
          <Text>Loading Explorer Assessment...</Text>
        </Stack>
      </Container>
    );
  }

  // Error state
  if (error) {
    return (
      <Container size="lg" py="xl">
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          <Text fw={500}>Error Loading Assessment</Text>
          <Text size="sm">{error}</Text>
          <Button mt="md" onClick={handleBackToInventory}>
            Return to Inventory
          </Button>
        </Alert>
      </Container>
    );
  }

  // Note: parentAssessmentUuid is optional - Explorer can run standalone without linked Builder

  return (
    <Container size="xl" py="lg">
      <Stack gap="lg">
        {/* Header */}
        <Card withBorder p="md" style={{ backgroundColor: 'rgba(16, 185, 129, 0.05)' }}>
          <Group justify="space-between" align="flex-start">
            <Stack gap="xs">
              <Group gap="md">
                <ActionIcon
                  variant="subtle"
                  onClick={handleBackToInventory}
                  title="Back to Inventory"
                >
                  <IconArrowLeft size={20} />
                </ActionIcon>
                <ThemeIcon size="lg" variant="gradient" gradient={{ from: 'grape', to: 'violet' }}>
                  <IconCompass size={20} />
                </ThemeIcon>
                <Stack gap={0}>
                  <Text fw={600} size="lg">Explorer Assessment</Text>
                  <Text size="sm" c="dimmed">{modelName}</Text>
                </Stack>
              </Group>
            </Stack>

            <Group gap="md">
              <Badge color="grape" variant="light" size="lg">
                Risk Analysis
              </Badge>
              <Tooltip label={autonomous ? 'Autonomous mode: Auto-advance through areas' : 'Manual mode: Approve each step'}>
                <Switch
                  checked={autonomous}
                  onChange={(e) => setAutonomous(e.currentTarget.checked)}
                  label="Autonomous"
                  labelPosition="left"
                  size="sm"
                />
              </Tooltip>
            </Group>
          </Group>
        </Card>

        {/* Stepper */}
        <Card withBorder p="md">
          <Stepper
            active={currentStepIndex}
            onStepClick={(idx) => handleStepClick(steps[idx].key)}
            color="grape"
            size="sm"
          >
            {steps.map((step) => (
              <Stepper.Step
                key={step.key}
                label={step.label}
                icon={<step.icon size={18} />}
                completedIcon={<IconCheck size={18} />}
              />
            ))}
          </Stepper>
        </Card>

        {/* Step 1: Describe Your Business */}
        {currentStep === 'describeBusinessCase' && (
          <Card withBorder p="lg">
            <Stack gap="md">
              <Group gap="md">
                <ThemeIcon size="lg" variant="light" color="green">
                  <IconBuildingBank size={20} />
                </ThemeIcon>
                <Stack gap={0}>
                  <Text fw={600}>Describe Your Business</Text>
                  <Text size="sm" c="dimmed">
                    Describe your organization — its industry, size, regulatory environment, and customer base
                  </Text>
                </Stack>
              </Group>

              <Divider />

              {/* Dynamic fields from template, or fallback to hardcoded textarea */}
              {templateContextFields && templateContextFields.length > 0 ? (
                <StepContent
                  modelId={modelId}
                  assessmentUuid={assessmentUuid}
                  section="businessProfile"
                  sectionConfig={templateContextFields}
                />
              ) : (
                <Textarea
                  label="Business Description"
                  description="Describe the organization: industry, size, main products/services, customer base, and regulatory environment"
                  placeholder="e.g., We are a mid-size fintech company with 500 employees, providing AI-powered credit scoring to retail banks across the EU..."
                  value={businessCaseDescription}
                  onChange={(e) => setBusinessCaseDescription(e.currentTarget.value)}
                  minRows={5}
                  maxRows={12}
                  autosize
                  required
                />
              )}

              <DocumentUpload
                modelId={modelId}
                documentType="business_case"
                bucket="business-case-docs"
                label="Business Case Documentation"
                description="Upload business plans, regulatory filings, impact assessments, etc."
                existingDocuments={businessCaseDocuments}
                onUploadComplete={(doc) => setBusinessCaseDocuments(prev => [...prev, doc])}
                onDocumentDeleted={(id) => setBusinessCaseDocuments(prev => prev.filter(d => d.id !== id))}
              />

              <Group justify="flex-end">
                <Button
                  color="green"
                  onClick={handleBusinessCaseSubmit}
                  disabled={templateContextFields ? !businessProfileData?.businessDescription?.trim() : !businessCaseDescription.trim()}
                  rightSection={<IconCheck size={16} />}
                >
                  Continue to Analysis
                </Button>
              </Group>
            </Stack>
          </Card>
        )}

        {/* Step 2: Context & Narrative */}
        {currentStep === 'narrative' && (
          <ExplorerNarrativeCard
            modelId={modelId}
            assessmentUuid={assessmentUuid}
            parentAssessmentUuid={parentAssessmentUuid}
            bucket={inheritedBucket}
            path={inheritedPath}
            businessContext={templateContextFields ? JSON.stringify(businessProfileData) : (businessCaseDescription || businessContext)}
            useCase={useCase}
            riskFormData={explorerRiskAreas}
            onApproved={handleNarrativeApproved}
          />
        )}

        {currentStep === 'risks' && (
          <>
            {/* Assessment Tracker Card */}
            <Card withBorder p="md" style={{ backgroundColor: 'rgba(174, 62, 201, 0.05)' }}>
              <Group justify="space-between" align="center">
                <Group gap="md">
                  <RingProgress
                    sections={[{
                      value: (currentAreaProgress.completedAreas / currentAreaProgress.totalAreas) * 100,
                      color: 'grape'
                    }]}
                    label={
                      <Text ta="center" size="xs" fw={600}>
                        {currentAreaProgress.completedAreas}/{currentAreaProgress.totalAreas}
                      </Text>
                    }
                    size={60}
                    thickness={6}
                  />
                  <Stack gap={2}>
                    <Text fw={600}>Risk Analysis</Text>
                    <Text size="sm" c="dimmed">
                      {currentAreaProgress.currentArea
                        ? `Processing: ${explorerRiskAreas[currentAreaProgress.currentArea]?.label || currentAreaProgress.currentArea}`
                        : currentAreaProgress.completedAreas === currentAreaProgress.totalAreas
                          ? 'All areas completed'
                          : 'Starting analysis...'}
                    </Text>
                    {currentAreaProgress.phase && (
                      <Badge size="xs" variant="light" color="blue">
                        {currentAreaProgress.phase}
                      </Badge>
                    )}
                  </Stack>
                </Group>
                <Button
                  variant="light"
                  size="xs"
                  onClick={() => setCurrentStep('narrative')}
                  leftSection={<IconArrowLeft size={14} />}
                >
                  Back to Narrative
                </Button>
              </Group>
            </Card>

            {/* Risk Processing Component */}
            <ExplorerRiskAutopilot
              modelId={modelId}
              assessmentUuid={assessmentUuid}
              parentAssessmentUuid={parentAssessmentUuid}
              businessContext={templateContextFields ? JSON.stringify(businessProfileData) : (businessCaseDescription || businessContext)}
              approvedNarrative={approvedNarrative}
              autonomous={autonomous}
              onAreaProgress={setCurrentAreaProgress}
              onComplete={handleRiskAnalysisComplete}
            />
          </>
        )}

        {currentStep === 'review' && (
          <Card withBorder p="lg">
            <Stack gap="md">
              <Group gap="md" justify="space-between">
                <Group gap="md">
                  <ThemeIcon size="lg" variant="light" color="grape">
                    <IconCheck size={20} />
                  </ThemeIcon>
                  <Stack gap={0}>
                    <Text fw={600}>Review & Refine</Text>
                    <Text size="sm" c="dimmed">
                      Review identified risks across all organizational areas
                    </Text>
                  </Stack>
                </Group>
                <Badge size="lg" color="grape" variant="light">
                  {savedRisks.length} Risks Identified
                </Badge>
              </Group>

              <Divider />

              {/* Summary Statistics */}
              <SimpleGrid cols={3} spacing="md">
                <Paper withBorder p="md" radius="md">
                  <Group gap="xs">
                    <ThemeIcon size="sm" variant="light" color="blue">
                      <IconChartBar size={14} />
                    </ThemeIcon>
                    <Text size="sm" fw={500}>Areas Analyzed</Text>
                  </Group>
                  <Text size="xl" fw={700} c="blue" mt="xs">
                    {Object.keys(explorerRisks).filter(k => explorerRisks[k]?.areaStatus === 'completed').length} / 6
                  </Text>
                </Paper>
                <Paper withBorder p="md" radius="md">
                  <Group gap="xs">
                    <ThemeIcon size="sm" variant="light" color="grape">
                      <IconAlertCircle size={14} />
                    </ThemeIcon>
                    <Text size="sm" fw={500}>Total Risks</Text>
                  </Group>
                  <Text size="xl" fw={700} c="grape" mt="xs">
                    {savedRisks.length}
                  </Text>
                </Paper>
                <Paper withBorder p="md" radius="md">
                  <Group gap="xs">
                    <ThemeIcon size="sm" variant="light" color="green">
                      <IconShieldCheck size={14} />
                    </ThemeIcon>
                    <Text size="sm" fw={500}>Risk Categories</Text>
                  </Group>
                  <Text size="xl" fw={700} c="green" mt="xs">
                    {new Set(savedRisks.map(r => r.category)).size}
                  </Text>
                </Paper>
              </SimpleGrid>

              {/* Risks by Area */}
              <ScrollArea.Autosize mah={500}>
                <Accordion variant="separated" radius="md">
                  {Object.entries(explorerRisks).map(([areaKey, areaData]) => {
                    const areaRisks = areaData?.areaRisks || {};
                    const riskCount = Object.values(areaRisks).flat().filter(r => r.status === 'Saved').length;
                    const areaLabel = explorerRiskAreas[areaKey]?.label || areaKey;

                    return (
                      <Accordion.Item key={areaKey} value={areaKey}>
                        <Accordion.Control>
                          <Group justify="space-between" pr="md">
                            <Text fw={500}>{areaLabel}</Text>
                            <Badge
                              color={riskCount > 0 ? 'grape' : 'gray'}
                              variant="light"
                              size="sm"
                            >
                              {riskCount} risks
                            </Badge>
                          </Group>
                        </Accordion.Control>
                        <Accordion.Panel>
                          {riskCount === 0 ? (
                            <Text size="sm" c="dimmed" fs="italic">
                              No risks identified in this area
                            </Text>
                          ) : (
                            <Stack gap="xs">
                              {Object.entries(areaRisks).map(([category, risks]) =>
                                risks.filter(r => r.status === 'Saved').map(risk => (
                                  <Paper key={risk.riskId} withBorder p="sm" radius="sm">
                                    <Group justify="space-between" mb="xs">
                                      <Text size="sm" fw={500}>{risk.riskName}</Text>
                                      {risk.priority && (
                                        <Badge
                                          size="xs"
                                          color={
                                            risk.priority === 'High' ? 'red' :
                                            risk.priority === 'Medium' ? 'orange' : 'green'
                                          }
                                        >
                                          {risk.priority}
                                        </Badge>
                                      )}
                                    </Group>
                                    <Group gap="xs">
                                      <Badge size="xs" variant="outline" color="gray">
                                        {category}
                                      </Badge>
                                      {risk.confidence && (
                                        <Badge size="xs" variant="dot" color="blue">
                                          {Math.round(risk.confidence * 100)}% confidence
                                        </Badge>
                                      )}
                                    </Group>
                                    {risk.rationale && (
                                      <Text size="xs" c="dimmed" mt="xs" lineClamp={2}>
                                        {risk.rationale}
                                      </Text>
                                    )}
                                  </Paper>
                                ))
                              )}
                            </Stack>
                          )}
                        </Accordion.Panel>
                      </Accordion.Item>
                    );
                  })}
                </Accordion>
              </ScrollArea.Autosize>

              {savedRisks.length === 0 && (
                <Alert color="yellow" variant="light" icon={<IconAlertCircle size={16} />}>
                  <Text size="sm">
                    No risks have been identified yet. This may indicate:
                  </Text>
                  <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                    <li>Risk analysis is still in progress</li>
                    <li>There was an issue with risk discovery</li>
                    <li>The assessment needs to be re-run</li>
                  </ul>
                  <Button
                    size="xs"
                    variant="light"
                    color="yellow"
                    mt="xs"
                    onClick={() => setCurrentStep('risks')}
                  >
                    Return to Risk Analysis
                  </Button>
                </Alert>
              )}

              <Group justify="flex-end">
                <Button
                  variant="light"
                  onClick={() => setCurrentStep('risks')}
                  leftSection={<IconArrowLeft size={16} />}
                >
                  Back to Risk Analysis
                </Button>
                <Button
                  color="grape"
                  onClick={() => setCurrentStep('report')}
                  rightSection={<IconFileText size={16} />}
                  disabled={savedRisks.length === 0}
                >
                  Generate Report
                </Button>
              </Group>
            </Stack>
          </Card>
        )}

        {currentStep === 'report' && (
          <Card withBorder p="lg">
            <Stack gap="md">
              <Group gap="md">
                <ThemeIcon size="lg" variant="light" color="grape">
                  <IconFileText size={20} />
                </ThemeIcon>
                <Stack gap={0}>
                  <Text fw={600}>Generate Report</Text>
                  <Text size="sm" c="dimmed">
                    Export Explorer assessment as a comprehensive report
                  </Text>
                </Stack>
              </Group>

              <Divider />

              <Alert color="grape" variant="light">
                <Text size="sm">
                  The report will include:
                </Text>
                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  <li>Inherited technical risks from Builder assessment</li>
                  <li>Risk narrative and context</li>
                  <li>Identified risks by area</li>
                  <li>Suggested controls and mitigations</li>
                  <li>Residual risk assessments</li>
                  <li>Recommendations for stakeholders</li>
                </ul>
              </Alert>

              <Group justify="flex-end">
                <Button
                  variant="light"
                  onClick={() => setCurrentStep('review')}
                  leftSection={<IconArrowLeft size={16} />}
                >
                  Back to Review
                </Button>
                <Button
                  color="grape"
                  leftSection={<IconFileText size={16} />}
                  onClick={() => navigate(`/explorer-report/${modelId}/${assessmentUuid}`)}
                >
                  View Report
                </Button>
              </Group>
            </Stack>
          </Card>
        )}
      </Stack>
    </Container>
  );
}
